import { ref, computed } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import { useI18n } from 'vue-i18n';

import type { SelectOption } from '@/components/common/types/selectOptionTypes';

import type { CcmGeneratedGridpoint } from '@/components/map/cnetConstructorMode/types/ccmTypes';


type UseCnetSubGridsResult = {
	showSubgridsUi: Ref<boolean>;
	subGridCount: Ref<number>;
	currentSubGrid: Ref<number>;
	subGridsOptions: ComputedRef<SelectOption[]>;
	allowDeleteSubGrid: ComputedRef<boolean>;
	addSubGrid: () => void;
	deleteSubGrid: () => void;
	resetSubGrids: () => void;
};

const useCnetSubGrids = (
	rawGridpoints: Ref<CcmGeneratedGridpoint[]>,
	gridpointsStatuses: Ref<Map<string, boolean>>,
): UseCnetSubGridsResult => {
	const { t } = useI18n();

	const currentSubGrid = ref(0);
	const showSubgridsUi = ref(false);
	const subGridCount = ref(1);

	const subGridsOptions = computed((): SelectOption[] => Array.from(
		{ length: subGridCount.value },
		(_, index) => ({
			label: t('cnet-constructor.legend.step3SubGridLabel', { n: index + 1 }),
			value: index,
		}),
	));

	const allowDeleteSubGrid = computed(() => subGridCount.value > 1 && currentSubGrid.value === subGridCount.value - 1);

	const addSubGrid = (): void => {
		subGridCount.value++;
		currentSubGrid.value = subGridCount.value - 1;
		rawGridpoints.value.forEach(gridpoint => {
			const key = `${gridpoint.index_lon},${gridpoint.index_lat},${currentSubGrid.value}`;
			if (!gridpointsStatuses.value.has(key)) {
				gridpointsStatuses.value.set(key, true);
			}
		});
	};

	const deleteSubGrid = (): void => {
		subGridCount.value--;
		rawGridpoints.value.forEach(gridpoint => {
			const key = `${gridpoint.index_lon},${gridpoint.index_lat},${currentSubGrid.value}`;
			if (gridpointsStatuses.value.has(key)) {
				gridpointsStatuses.value.delete(key);
			}
		});
		currentSubGrid.value = subGridCount.value - 1;
	};

	const resetSubGrids = (): void => {
		currentSubGrid.value = 0;
		subGridCount.value = 1;
	};

	return {
		showSubgridsUi,
		subGridCount,
		currentSubGrid,
		subGridsOptions,
		allowDeleteSubGrid,
		addSubGrid,
		deleteSubGrid,
		resetSubGrids,
	};
};

export {
	useCnetSubGrids,
};
